import {
    useCallback, useMemo,
} from 'react'
import BatchActionPopupWrapper from 'app/shared-components/BatchActions/BatchActionPopupWrapper'
import CsvUpload from 'app/shared-components/BatchActions/CsvUpload'
import {
    BatchEntity,
} from 'app/enums/common.enums'
import useLabel from 'app/hooks/useLabel'
import useValueMapping from 'app/requests/ValueMapping/hooks/useValueMapping'

import forecastRequests from 'app/requests/Forecast/Forecast.requests'
import {
    validateForecastCsvHeaders,
} from './utils/validateForecastCsv'
import {
    ForecastCreatePreparedData,
    ForecastCreateRawData,
} from './ShortTermForecastCsvUpload.types'
import parseForecastCsvData from './utils/parseForecastCsvData'
import feedbackFormFields from './ShortTermForecastCsvUpload.fields'
import getColumns from './ShortTermForecastCsvUpload.columns'
import getDomainMapping from './utils/getDomainMapping'
import {
    mandatoryHeaders,
} from './ShortTermForecast.constants'
import mapUploadParams from './utils/mapUploadParams'

const ShortTermForecastCsvUpload = () => {
    const l = useLabel()

    const errorFeedbackColumns = useMemo(() => {
        return getColumns(l)
    }, [l])

    const getValueMapping = useValueMapping({
        configId: 'short-term-forecast-csv-upload',
    })

    const parseFileData = useCallback(
        async (data: ForecastCreateRawData[]): Promise<ForecastCreatePreparedData[]> => {
            const domainMapping = getDomainMapping(data)
            const idMapping = await getValueMapping(domainMapping)

            if (idMapping.size !== domainMapping.size) {
                throw new Error(l('ERROR_RETRIEVING_MAPPINGS_CHECK_CSV'))
            }

            return parseForecastCsvData(data, idMapping)
        }, [
            getValueMapping,
            l,
        ],
    )
    const uploadParamsMapper = useCallback(
        (record: ForecastCreatePreparedData) => {
            return forecastRequests.create(mapUploadParams(record))
        }, [],
    )
    const validateData = useCallback(
        (data: ForecastCreateRawData[]) => {
            return validateForecastCsvHeaders(data, l)
        }, [l],
    )

    const renderChildren = useCallback(({
        closePopup,
    }) => {
        return (
            <CsvUpload<ForecastCreateRawData, ForecastCreatePreparedData>
                entity={BatchEntity.FORECAST}
                uniqField="tempId"
                parseDataOnLoad={parseFileData}
                validateData={validateData}
                mandatoryHeaders={mandatoryHeaders}
                feedbackFormFields={feedbackFormFields}
                errorFeedbackColumns={errorFeedbackColumns}
                uploadParamsMapper={uploadParamsMapper}
                onCancel={closePopup}
            />
        )
    }, [
        errorFeedbackColumns,
        parseFileData,
        uploadParamsMapper,
        validateData,
    ])

    return (
        <BatchActionPopupWrapper
            dialogTitle={l('BULK_CREATE')}
            renderChildren={renderChildren}
        />
    )
}

export default ShortTermForecastCsvUpload
